import { useState, useRef, useEffect } from "react";
import PageContainer, { PageHeader } from "@/components/PageContainer";
import { API as API_BASE } from "@/lib/api";
import { Sparkles, Send, User } from "lucide-react";

const SUGGESTIONS = [
  "How do I map SIMOR into a Level 3.5 residential group agenda?",
  "Which frameworks does a BHT actually use day to day?",
  "What's the right MBC cadence at every LOC transition?",
  "Explain the difference between Tier 2 and Tier 3 for a 3-site operator.",
];

function genSessionId() {
  return `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function AskPlaybook() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(genSessionId);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const sendMessage = async (text) => {
    if (!text.trim() || streaming) return;
    const userMsg = { role: "user", content: text };
    const history = [...messages, userMsg];
    setMessages([...history, { role: "assistant", content: "", streaming: true }]);
    setInput("");
    setStreaming(true);

    try {
      const response = await fetch(`${API_BASE}/chat/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Chat error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistant = "";

      // Read Server-Sent Events; each event is "data: <text>\n\n"
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const payload = part.slice(5).trimStart();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              assistant += parsed.delta;
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: "assistant", content: assistant, streaming: true };
                return next;
              });
            } else if (parsed.error) {
              assistant += `\n\n_Error: ${parsed.error}_`;
            }
          } catch {
            // ignore malformed
          }
        }
      }

      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: assistant || "(no response)", streaming: false };
        return next;
      });
    } catch (err) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: `The assistant is temporarily unavailable. ${err.message}`,
          streaming: false,
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <PageContainer testid="ask-playbook-page" wide>
      <PageHeader
        overline="Portal · Assistant"
        title="Ask the Playbook."
        subtitle="Claude Sonnet 4.5 grounded in the Catalyst Continuum. Ask about frameworks, roles, levels of care, or pricing."
      />

      <div className="card overflow-hidden flex flex-col" style={{ height: "70vh" }} data-testid="chat-container">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-5 no-scrollbar" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-md bg-teal text-cream flex items-center justify-center">
                  <Sparkles size={16} strokeWidth={2} />
                </div>
                <div className="text-sm text-ink-muted">Try one of these to start:</div>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="text-left card p-4 card-hover text-sm text-navy"
                    data-testid={`chat-suggestion-${i}`}
                    disabled={streaming}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className="flex gap-3" data-testid={`chat-msg-${m.role}-${i}`}>
              <div
                className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                  m.role === "user" ? "bg-navy text-cream" : "bg-teal text-cream"
                }`}
              >
                {m.role === "user" ? <User size={14} strokeWidth={2} /> : <Sparkles size={14} strokeWidth={2} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-ink-muted mb-1">
                  {m.role === "user" ? "You" : "Playbook Assistant · Claude Sonnet 4.5"}
                </div>
                <div className={`text-sm text-ink leading-relaxed whitespace-pre-wrap ${m.streaming ? "stream-cursor" : ""}`}>
                  {m.content}
                </div>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="border-t border-line-soft p-4 flex gap-2" data-testid="chat-input-form">
          <input
            className="field flex-1"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about frameworks, roles, levels of care, or pricing..."
            data-testid="chat-input"
            disabled={streaming}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={streaming || !input.trim()}
            data-testid="chat-send"
          >
            <Send size={14} strokeWidth={2} />
            {streaming ? "Thinking..." : "Send"}
          </button>
        </form>
      </div>

      <p className="text-xs text-ink-muted mt-3">
        Answers are grounded in the Continuum. Not clinical advice. Verify against your organization's policies.
      </p>
    </PageContainer>
  );
}
